export const autoSizeHidingPriorityAttributeName = 'data-auto-size-priority'
export type AutoSizeHidingPriority = 'alwaysVisible' | 'alwaysHidden' | number
export type AutoSizeHidingPriorityAttributeValue = 'alwaysVisible' | 'alwaysHidden' | `${number}`
export interface AutoSizeItem {
  width: number
  hidingPriority?: AutoSizeHidingPriority
}
export interface AutoSizeParams {
  availableWidth: number
  items: AutoSizeItem[]
  moreItemWidth: number
  minVisibleItems?: number
  maxVisibleItems?: number
  minHiddenItems?: number
  considerMoreAsVisibleItem?: boolean
  maxVisibleLines?: number
}
export interface AutoSizeResult {
  indexesToHide: number[]
  indexesToShow: number[]
  maxNeededWidth: number
  isMoreVisible: boolean
}

// Tolerance in pixels to compensate for sub-pixel rounding at non-integer browser zoom levels (e.g. 110%).
// The sum of individual getBoundingClientRect widths can exceed the container width by fractions of a pixel.
const SUBPIXEL_TOLERANCE = 1

// Mirrors flex-wrap layout; an item wider than the container overflows regardless of the lines limit
const fitsInLines = (widths: number[], availableWidth: number, maxLines: number) => {
  const lineCapacity = availableWidth + SUBPIXEL_TOLERANCE
  // The browser wraps at the exact width, so the tolerance would let an item overflow onto an extra line
  const lineBreakWidth = maxLines > 1 ? availableWidth : lineCapacity
  let lines = 1
  let lineWidth = 0
  for (const width of widths) {
    if (width > lineCapacity) {
      return false
    }
    if (lineWidth > 0 && lineWidth + width > lineBreakWidth) {
      lines += 1
      lineWidth = 0
    }
    lineWidth += width
  }
  return lines <= maxLines
}

export const autoSize = ({
  availableWidth,
  items,
  moreItemWidth,
  minVisibleItems = 0,
  maxVisibleItems = Infinity,
  minHiddenItems = 0,
  considerMoreAsVisibleItem = false,
  maxVisibleLines = 1,
}: AutoSizeParams): AutoSizeResult => {
  if (items.length === 0) {
    return { indexesToHide: [], indexesToShow: [], maxNeededWidth: 0, isMoreVisible: false }
  }

  const alwaysVisibleIndexes: number[] = []
  const regularItems: { priority: number; index: number }[] = []
  let alwaysVisibleItemWidthsSum = 0
  let shownRegularItemWidthSum = 0
  const indexesToHide: number[] = []

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    const p = item.hidingPriority

    if (p === 'alwaysVisible') {
      alwaysVisibleIndexes.push(i)
      alwaysVisibleItemWidthsSum += item.width
    } else if (p === 'alwaysHidden') {
      indexesToHide.push(i)
    } else {
      const priority = p || 0
      regularItems.push({ priority, index: i })
      shownRegularItemWidthSum += item.width
    }
  }

  regularItems.sort((a, b) => a.priority - b.priority)

  const regularIndexesToShow: number[] = regularItems.map((r) => r.index)

  const fitsAvailableSpace = () => {
    const shownWidths = [...alwaysVisibleIndexes, ...regularIndexesToShow]
      .sort((a, b) => a - b)
      .map((index) => items[index].width)
    if (indexesToHide.length > 0) {
      shownWidths.push(moreItemWidth)
    }
    return fitsInLines(shownWidths, availableWidth, maxVisibleLines)
  }

  let maxNeededWidth = 0

  for (let i = regularIndexesToShow.length - 1; i >= 0; i--) {
    const index = regularIndexesToShow[i]
    let currentWidth = alwaysVisibleItemWidthsSum + shownRegularItemWidthSum
    if (indexesToHide.length > 0) {
      currentWidth += moreItemWidth
    }

    // When the More item is shown it takes one of the visible slots, so both bounds shift by 1.
    const moreDropdownShouldTakeSlot = considerMoreAsVisibleItem && indexesToHide.length > 0
    const effectiveMaxVisibleItems = moreDropdownShouldTakeSlot ? maxVisibleItems - 1 : maxVisibleItems
    const effectiveMinVisibleItems = moreDropdownShouldTakeSlot ? minVisibleItems - 1 : minVisibleItems

    const minVisibleItemsReached = regularIndexesToShow.length + alwaysVisibleIndexes.length <= effectiveMinVisibleItems
    const maxVisibleItemsExceeded = regularIndexesToShow.length + alwaysVisibleIndexes.length > effectiveMaxVisibleItems

    if (!maxVisibleItemsExceeded) {
      maxNeededWidth = Math.max(maxNeededWidth, currentWidth)
    }

    if ((!minVisibleItemsReached && !fitsAvailableSpace()) || maxVisibleItemsExceeded) {
      indexesToHide.push(index)
      regularIndexesToShow.pop()
      shownRegularItemWidthSum -= items[index].width
    }
  }

  if (indexesToHide.length > 0) {
    // The More item is shown here, so it takes one of the visible slots when considerMoreAsVisibleItem is set.
    const effectiveMinVisibleItems = considerMoreAsVisibleItem ? minVisibleItems - 1 : minVisibleItems

    const extraToHide = Math.min(
      minHiddenItems - indexesToHide.length, // how many more are needed
      regularIndexesToShow.length + alwaysVisibleIndexes.length - effectiveMinVisibleItems, // how many we may still hide
      regularIndexesToShow.length, // never more than what is left shown
    )

    if (extraToHide > 0) {
      indexesToHide.push(...regularIndexesToShow.splice(regularIndexesToShow.length - extraToHide))
    }
  }

  return {
    indexesToHide: indexesToHide.sort((a, b) => a - b),
    indexesToShow: [...alwaysVisibleIndexes, ...regularIndexesToShow].sort((a, b) => a - b),
    maxNeededWidth,
    isMoreVisible: indexesToHide.length > 0,
  }
}
